import { useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useKeyboard } from "@/lib/KeyboardContext";
import { Keyboard, Save, XCircle } from "lucide-react";

// Height reserved at the bottom of the dialog when the global on-screen
// keyboard is open, so the sticky footer (Καταχώρηση / Ακύρωση) is never
// hidden behind the keyboard panel. Must match VirtualKeyboard's rendered
// height (header ~40px + 4 rows × ~70px + paddings).
const KB_RESERVE_PX = 380;

/**
 * Large full-screen comment editor for items that have ``allow_comment=true``
 * but NO ``comment_group_ids`` assigned in the catalog. The rich tile-based
 * picker (CommentInlinePanel) is wrong for these: it would either show no
 * tiles at all or — worse — fall back to an unrelated group.
 *
 * Layout: title with item description, textarea + on-screen keyboard
 * toggle, footer with Ακύρωση/Επιστροφή + Καταχώρηση buttons.
 *
 * Used by Restaurant.jsx, POSRetail.jsx and DeliveryNewOrder.jsx — the
 * caller is responsible for opening it (`open=true` + `item`) and for the
 * onSave / onCancel callbacks.
 */
export default function SimpleCommentDialog({ open, onOpenChange, item, initialText = "", onSave }) {
  const { open: kbOpen, toggle: kbToggle, setOpen: kbSetOpen } = useKeyboard();
  const taRef = useRef(null);
  const stateRef = useRef({ value: initialText });

  useEffect(() => {
    if (open) {
      stateRef.current.value = initialText;
      if (taRef.current) {
        taRef.current.value = initialText;
        setTimeout(() => taRef.current?.focus(), 100);
      }
    }
  }, [open, initialText]);

  // Whenever the OSK is toggled open (or the dialog opens with OSK already
  // visible), re-focus the textarea. The VirtualKeyboard tracks the active
  // input through a `focusin` listener that mounts only when it opens, so we
  // must refire focusin AFTER it mounts otherwise the first keystrokes go
  // nowhere.
  useEffect(() => {
    if (!open) return;
    if (!kbOpen) return;
    const t = setTimeout(() => {
      const el = taRef.current;
      if (!el) return;
      el.focus({ preventScroll: true });
      // Place caret at end so subsequent characters append naturally.
      try {
        const len = el.value.length;
        el.setSelectionRange(len, len);
      } catch (_e) { /* noop */ }
    }, 50);
    return () => clearTimeout(t);
  }, [kbOpen, open]);

  const cancel = () => {
    // Don't surface free-text on cancel — purge stale state.
    stateRef.current.value = "";
    kbSetOpen(false);
    onOpenChange(false);
  };

  const save = () => {
    onSave?.((stateRef.current.value || "").trim());
    kbSetOpen(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-none w-screen sm:rounded-none p-0 gap-0 flex flex-col bg-slate-50 !top-0 !translate-y-0"
        style={{ height: kbOpen ? `calc(100dvh - ${KB_RESERVE_PX}px)` : "100dvh" }}
        hideClose
        data-testid="simple-comment-dialog"
        onPointerDownOutside={(e) => {
          // The global VirtualKeyboard is portalled OUTSIDE this dialog's
          // subtree; without this guard Radix treats every key tap as an
          // "outside" interaction and dismisses the dialog. We keep that
          // behaviour for genuine outside clicks (overlay) but whitelist
          // any element living under [data-testid="virtual-keyboard"].
          if (e.target?.closest?.('[data-testid="virtual-keyboard"]')) {
            e.preventDefault();
          }
        }}
        onInteractOutside={(e) => {
          if (e.target?.closest?.('[data-testid="virtual-keyboard"]')) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader className="px-6 py-4 border-b-2 border-slate-200 bg-white shrink-0">
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            Σχόλιο για:{" "}
            <span className="text-blue-700">{item?.description || "—"}</span>
          </DialogTitle>
        </DialogHeader>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="max-w-3xl mx-auto space-y-4">
            <p className="text-sm text-slate-600">
              Γράψε ελεύθερο σχόλιο για το είδος. Θα τυπωθεί ως
              πρόσθετη γραμμή κάτω από το προϊόν στην εκτύπωση κουζίνας
              και στις παρατηρήσεις της απόδειξης.
            </p>
            <div className="flex items-start gap-3">
              <textarea
                ref={taRef}
                defaultValue={initialText}
                onChange={(e) => { stateRef.current.value = e.target.value; }}
                placeholder="π.χ. χωρίς αλάτι, ψημένο μέτριο, για παιδί..."
                rows={6}
                className="flex-1 p-4 rounded-lg border-2 border-slate-300 bg-white text-xl leading-relaxed focus:border-blue-500 focus:outline-none resize-none font-medium"
                data-testid="simple-comment-textarea"
                maxLength={300}
              />
              <Button
                type="button"
                onClick={kbToggle}
                variant="outline"
                size="lg"
                className={`h-14 w-14 p-0 border-2 ${
                  kbOpen ? "border-blue-500 bg-blue-50" : "border-slate-300 bg-white"
                }`}
                title={kbOpen ? "Κλείσιμο πληκτρολογίου" : "Άνοιγμα πληκτρολογίου"}
                data-testid="simple-comment-osk-btn"
              >
                <Keyboard size={22} className={kbOpen ? "text-blue-700" : "text-slate-700"} />
              </Button>
            </div>
            <div className="text-xs text-slate-500">
              Μέχρι 300 χαρακτήρες.
            </div>
          </div>
        </div>

        {/* Sticky footer — must sit ABOVE the global VirtualKeyboard panel
            (which uses z-[60]) so the touch operator can always reach the
            primary actions. */}
        <div
          className="px-6 py-4 border-t-2 border-slate-200 bg-white shrink-0 flex items-center justify-end gap-3 relative z-[70]"
        >
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={cancel}
            className="h-12 font-bold border-2 px-6 border-red-300 text-red-700 hover:bg-red-50"
            data-testid="simple-comment-cancel"
          >
            <XCircle size={16} className="mr-1.5" /> Ακύρωση / Επιστροφή
          </Button>
          <Button
            size="lg"
            onClick={save}
            className="bg-emerald-600 hover:bg-emerald-700 h-12 font-bold px-8"
            data-testid="simple-comment-save"
          >
            <Save size={16} className="mr-1.5" /> Καταχώρηση
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
